import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  Flex,
  Icon,
  Pagination,
  Segmented,
  SkeletonBlock,
  SkeletonGroup,
} from '@bookora/ui';

import { listNotifications } from '@/api/endpoints/notifications';
import { queryKeys } from '@/api/query-keys';
import type { StaffNotification } from '@/api/types';
import { useAuth } from '@/auth/token-store';

import { NotificationRow } from './notification-row';
import { MARK_ALL_READ_LABEL, useMarkAllNotificationsRead } from './use-mark-all-read';

const PAGE_SIZE = 20;

type Filter = 'unread' | 'all';

/**
 * «همهٔ اعلان‌ها» — every notification, not just the bell's latest eight.
 *
 * Opens on «خوانده‌نشده» because that is what somebody arriving from the bell
 * is looking for: the booking request that scrolled out of the preview. «همه»
 * is one click away for «what was that alert this morning». Rows are the same
 * `NotificationRow` the popover draws, and following one marks it read and
 * navigates exactly as it does there.
 *
 * The server filters by permission before returning anything, as it does for
 * the bell — a receptionist never sees a low-stock alert whose numbers she has
 * no access to.
 *
 * «علامت‌گذاری همه به‌عنوان خوانده‌شده» shows only while `unreadTotal` (the bell's own
 * count, passed down so the two cannot disagree) is above zero.
 */
export function NotificationsDrawer({
  open,
  unreadTotal,
  onClose,
  onOpenItem,
}: {
  open: boolean;
  /** The bell's unread count. */
  unreadTotal: number;
  onClose: () => void;
  onOpenItem: (item: StaffNotification) => void;
}) {
  const { activeClinicId } = useAuth();
  const [filter, setFilter] = useState<Filter>('unread');
  const [page, setPage] = useState(1);

  // Every opening starts on the first page of the unread ones. This component
  // outlives the drawer (only the drawer's CONTENT is destroyed on close), so
  // without the reset a second opening landed on yesterday's «همه», page 3.
  const [openedAt, setOpenedAt] = useState(open);
  if (open !== openedAt) {
    setOpenedAt(open);
    if (open) {
      setFilter('unread');
      setPage(1);
    }
  }

  const list = useQuery({
    queryKey: [...queryKeys.notifications.list(activeClinicId, page, PAGE_SIZE), filter] as const,
    queryFn: ({ signal }) =>
      listNotifications(
        { page, pageSize: PAGE_SIZE, ...(filter === 'unread' ? { unreadOnly: true } : {}) },
        signal,
      ),
    enabled: open && Boolean(activeClinicId),
    placeholderData: (previous) => previous,
  });

  const markAll = useMarkAllNotificationsRead();

  const items = list.data?.items ?? [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="همهٔ اعلان‌ها"
      width={440}
      destroyOnHidden
      extra={
        unreadTotal > 0 ? (
          <Button
            type="link"
            size="small"
            icon={<Icon name="check" size={16} />}
            loading={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            {MARK_ALL_READ_LABEL}
          </Button>
        ) : null
      }
    >
      <Flex vertical gap={12}>
        <Segmented
          block
          aria-label="فیلتر اعلان‌ها"
          value={filter}
          onChange={(value) => {
            setFilter(value as Filter);
            setPage(1);
          }}
          options={[
            { label: 'خوانده‌نشده', value: 'unread' },
            { label: 'همه', value: 'all' },
          ]}
        />

        {list.isLoading ? (
          <SkeletonGroup label="در حال دریافت اعلان‌ها…" className="bk-notification-skeleton">
            {[0, 1, 2, 3].map((row) => (
              <div key={row} style={{ display: 'grid', gap: 'var(--bk-space-1-5)' }}>
                <SkeletonBlock shape="text" width={row % 2 ? '55%' : '70%'} />
                <SkeletonBlock shape="text" width="90%" />
              </div>
            ))}
          </SkeletonGroup>
        ) : list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} compact />
        ) : items.length === 0 ? (
          <EmptyState
            title={filter === 'unread' ? 'اعلان خوانده‌نشده‌ای ندارید' : 'هنوز اعلانی نیومده'}
            description={
              filter === 'unread' ? (
                <Button type="link" size="small" onClick={() => setFilter('all')}>
                  نمایش همهٔ اعلان‌ها
                </Button>
              ) : (
                'درخواست‌های نوبت، هشدار موجودی و پیام‌های سامانه اینجا جمع می‌شن.'
              )
            }
          />
        ) : (
          <ul
            className={[
              'bk-notification-panel__list',
              list.isPlaceholderData ? 'bk-data-table--refetching' : null,
            ]
              .filter(Boolean)
              .join(' ')}
            aria-busy={list.isFetching || undefined}
          >
            {items.map((item) => (
              <li key={item.id}>
                <NotificationRow item={item} onOpen={onOpenItem} />
              </li>
            ))}
          </ul>
        )}

        {(list.data?.pageCount ?? 0) > 1 ? (
          <Pagination
            size="small"
            align="center"
            current={page}
            pageSize={PAGE_SIZE}
            total={list.data?.total ?? 0}
            onChange={setPage}
            showSizeChanger={false}
          />
        ) : null}
      </Flex>
    </Drawer>
  );
}
